import omit from 'lodash/omit';

import { get as getConstants } from '@lumapps/constants';
import { canUserDesignContentType } from '@lumapps/content-types/ducks/selectors';
import { isContentWithContextualAction, isUserDirectory } from '@lumapps/content-types/utils';
import { isFeatureEnabled } from '@lumapps/features';
import {
    authorizations,
    canEditOnlyOwnContentAuthorizations,
    isInstanceSuperAdmin,
    isUserAllowed,
} from '@lumapps/permissions';
import { getPermissionKey } from '@lumapps/permissions/utils';
import { createSelector } from '@lumapps/redux/reselect';
import { AppId } from '@lumapps/router';
import { getSubscriptions, isConnected, userIdSelector } from '@lumapps/user/ducks/selectors';

import { REPLACE_PUBLISH_WITH_VALIDATE_ONLY_ROLE_FF } from '../constants';
import { Content, type WriteModeContent } from '../types';
import { getContentPublicationStatus } from '../utils/getContentPublicationStatus';
import { isUserEditorOfContent } from '../utils/isUserEditorOfContent';

const constants = getConstants();

const isDesignerApp = constants.applicationId === AppId.designer;
const internalContentSelector = (state: BaseStore) => state.content || {};

export const contentSelector = createSelector(internalContentSelector, (content) => content);

export const getCurrentContent = createSelector(
    contentSelector,
    (content) => omit(content, ['entities', 'nextContentId', 'actionState', 'currentContributionMode']) as Content,
);

/** Live revision is only available when editing a content. */
export const getCurrentContentLiveRevision = createSelector(contentSelector, (content) =>
    content.isDesignerMode ? content.liveRevision : undefined,
);

/**
 * Return whether the current content is the currently published homepage.
 * NB: in content editor, only the last published revision matters.
 */
export const isCurrentContentPublishedHomepage = createSelector(
    getCurrentContentLiveRevision,
    (liveRevision) => liveRevision?.isHomepage,
);
export const isInDesignerMode = createSelector(contentSelector, (content) => content.isDesignerMode || isDesignerApp);

export const getContentEntities = createSelector(contentSelector, (content) => content.entities);

export const getCurrentContentId = createSelector(contentSelector, (content) => content.id);

export const getCurrentContentType = createSelector(contentSelector, (content) => content.type);

export const getCurrentContentCanonicalUrl = createSelector(contentSelector, (content) => content.canonicalUrl);

export const getCurrentContentInstanceId = createSelector(contentSelector, (content) => content.instance);

export const getCurrentCustomContentTypeId = createSelector(contentSelector, (content) => content.customContentType);

export const getCurrentCustomContentTypeDetails = createSelector(
    contentSelector,
    (content) => content.customContentTypeDetails,
);

export const getCurrentContentExcerpt = createSelector(contentSelector, (content) => content.excerpt);

export const getCurrentContentMediaThumbnail = createSelector(contentSelector, (content) => content.mediaThumbnail);

export const getCurrentContentSlug = createSelector(contentSelector, (content) => content.slug);

export const getCurrentContentFeedKeys = createSelector(contentSelector, (content) => content.feedKeys);

export const getCurrentContentCommunityIds = createSelector(contentSelector, (content) => content.communityIds);

export const getCurrentContentStatus = createSelector(contentSelector, (content) => content.status);

export const getCurrentContentPublicationStatus = createSelector(
    [getCurrentContent, isInDesignerMode],
    (content, isDesignerMode) => {
        const revision =
            isDesignerMode && (content as WriteModeContent).liveRevision
                ? (content as WriteModeContent).liveRevision
                : content;
        const publicationStatus = getContentPublicationStatus({
            legacyStatus: revision?.status,
            lastRevision: revision?.lastRevision,
            startDate: revision?.startDate,
            endDate: revision?.endDate,
            lastPublishedAt: revision?.lastPublishedAt,
        });

        return publicationStatus;
    },
);

export const getCurrentContentTitle = createSelector(contentSelector, (content) => content.title);

/** Get the startDate and endDate of the current content */
export const getCurrentContentScheduledDatesSelector = createSelector(contentSelector, (content) => ({
    startDate: content.startDate,
    endDate: content.endDate,
    // The edited startDate and endDate. Comes from the legacy. Should only be used within the designer.
    currentStartDate: content.startDateInput,
    currentEndDate: content.endDateInput,
}));

export const getCurrentContentLastPublicationDate = createSelector(
    contentSelector,
    (content) => content.lastPublishedAt,
);

export const getCurrentContentExpirationDate = createSelector(contentSelector, (content) => content.endDate);

export const getCurrentContentScheduledDate = createSelector(contentSelector, (content) => content.startDate);

export const getCurrentContentProperties = createSelector(contentSelector, (content) => content.properties || {});

export const getCurrentContentLastRevision = createSelector(contentSelector, (content) => content.lastRevision);

export const getCurrentContentUpdatedDate = createSelector(
    contentSelector,
    (content) => content.updatedAt || content.lastRevision?.updatedAt,
);

export const getCurrentContentHeader = createSelector(contentSelector, (content) => content.headerDetails);

export const getCurrentContentHasCommentWidget = createSelector(contentSelector, (content) => content.hasCommentWidget);

export const isContentCurrentlyDisplayed = createSelector(
    contentSelector,
    (content) => Boolean(content) && Boolean(content.id),
);

export const isCurrentContentV2Compatible = createSelector(
    contentSelector,
    (content) => content.template?.isV2Compatible,
);

/**
 * Determines whether the current user has access to the given content.
 * It takes into account the permissions that the user has, evaluating if the user has access
 * to the edition of the custom content type of the content, or if the user has access
 * as an editor to the content.
 */
export const isEditorOfContent = createSelector(
    getSubscriptions,
    authorizations,
    canEditOnlyOwnContentAuthorizations,
    userIdSelector,
    isInstanceSuperAdmin,
    (subscriptions, auths, canEditOnlyOwnContentAuths, currentUserId, isAdmin) => (content: Content) =>
        isUserEditorOfContent({
            content,
            subscriptions,
            auths,
            canEditOnlyOwnContentAuths,
            userId: currentUserId,
            isInstanceSuperAdmin: isAdmin,
        }),
);

/**
 * Determines whether the current user can delete the given content.
 * It takes into account the permissions that the user has, evaluating if the user has the given right
 * to delete the custom content type of the content, or if the user is an admin.
 */
export const canDeleteContent = (contentTypeId?: string) =>
    createSelector(
        isInstanceSuperAdmin,
        isUserAllowed(getPermissionKey('CUSTOM_CONTENT', 'DELETE', contentTypeId)),
        (isAdmin, hasDeleteRightOnContentType) => {
            if (!contentTypeId) {
                return false;
            }

            if (isAdmin) {
                return true;
            }

            return hasDeleteRightOnContentType;
        },
    );

/**
 * Determines whether the current user can edit any content item for the given CCT.
 * Returns false when no contentTypeId is provided (safe default for uninitialized state).
 */
export const canEditContent = (contentTypeId?: string) =>
    createSelector(
        isInstanceSuperAdmin,
        isUserAllowed(getPermissionKey('CUSTOM_CONTENT', 'EDIT', contentTypeId)),
        (isAdmin, hasEditRight) => {
            if (!contentTypeId) {
                return false;
            }
            if (isAdmin) {
                return true;
            }
            return hasEditRight;
        },
    );

/**
 * Determines whether the current user can publish content items for the given CCT.
 * Returns false when no contentTypeId is provided (safe default for uninitialized state).
 */
export const canPublishContent = (contentTypeId?: string) =>
    createSelector(
        isInstanceSuperAdmin,
        isUserAllowed(getPermissionKey('CUSTOM_CONTENT', 'PUBLISH', contentTypeId)),
        (isAdmin, hasPublishRight) => {
            if (!contentTypeId) {
                return false;
            }
            if (isAdmin) {
                return true;
            }
            return hasPublishRight;
        },
    );

/**
 * Determines whether the current user is a "validate-only" publisher for the given CCT:
 * the `replace-publish-with-validate-only-role` FF is enabled AND the user can publish but
 * cannot edit. Such users may review content pending validation (validate / request revision /
 * refuse) but must NOT directly publish or unpublish it.
 */
export const isValidateOnlyPublisher = (contentTypeId?: string) =>
    createSelector(
        isFeatureEnabled(REPLACE_PUBLISH_WITH_VALIDATE_ONLY_ROLE_FF),
        canPublishContent(contentTypeId),
        canEditContent(contentTypeId),
        (isValidateOnlyRoleEnabled, canPublish, canEdit) => isValidateOnlyRoleEnabled && canPublish && !canEdit,
    );

/**
 * Determines whether the current user can archive content items for the given CCT.
 * Returns false when no contentTypeId is provided (safe default for uninitialized state).
 */
export const canArchiveContent = (contentTypeId?: string) =>
    createSelector(
        isInstanceSuperAdmin,
        isUserAllowed(getPermissionKey('CUSTOM_CONTENT', 'ARCHIVE', contentTypeId)),
        (isAdmin, hasArchiveRight) => {
            if (!contentTypeId) {
                return false;
            }
            if (isAdmin) {
                return true;
            }
            return hasArchiveRight;
        },
    );

/**
 * Determines whether the current user has access to the current content displayed.
 * It takes into account the permissions that the user has, evaluating if the user has access
 * to the edition of the custom content type of the current content, or if the user has access
 * as an editor to the current content.
 */
export const isEditorOfCurrentContent = createSelector(
    contentSelector,
    getSubscriptions,
    authorizations,
    canEditOnlyOwnContentAuthorizations,
    userIdSelector,
    isInstanceSuperAdmin,
    (content, subscriptions, auths, canEditOnlyOwnContentAuths, currentUserId, isAdmin) =>
        isUserEditorOfContent({
            content,
            subscriptions,
            auths,
            canEditOnlyOwnContentAuths,
            userId: currentUserId,
            isInstanceSuperAdmin: isAdmin,
        }),
);

/**
 * True when the current user belongs to a group listed in the content's editors field.
 * Unlike isEditorOfCurrentContent, this does NOT include role-level EDIT authorization.
 */
export const isInCurrentContentEditorsList = createSelector(
    contentSelector,
    getSubscriptions,
    (content, subscriptions) => {
        const { editors } = content || {};

        if (!editors?.length || !subscriptions?.length) {
            return false;
        }

        return subscriptions.some((subscription) => editors.includes(subscription.feed));
    },
);

export const canUserDesignCurrentContent = (contentTypeId?: string) =>
    createSelector(
        [canUserDesignContentType(contentTypeId), isInCurrentContentEditorsList],
        (canDesignType, isInEditorsList) => canDesignType || isInEditorsList,
    );

export const canUserEditCurrentContent = createSelector(
    contentSelector,
    isContentCurrentlyDisplayed,
    isInstanceSuperAdmin,
    isConnected,
    isEditorOfCurrentContent,
    (content, isContentDisplayed, isAdmin, connected, isEditor) => {
        /**
         * If there is no user connected or we do not have any content in our store, the user
         * is not allowed to edit the content since there isn't one.
         */
        if (!connected || !isContentDisplayed) {
            return false;
        }

        const { type } = content;

        /**
         * User Directories are not editable
         */
        if (isUserDirectory(type)) {
            return false;
        }

        /**
         * if the user is super admin (of this instance or in general), they are always able to edit
         * any content.
         */
        if (isAdmin) {
            return true;
        }

        return content.canEdit || isEditor;
    },
);

export const isContextualActionsEnabled = isFeatureEnabled('contextual-actions');

export const contentHasContextualActions = createSelector(contentSelector, (content) =>
    isContentWithContextualAction(content.type),
);

/** Get the current contribution mode. */
export const getCurrentContributionMode = createSelector(contentSelector, (content) => content.currentContributionMode);

/** Contribution Alpha FF */
export const isContributionAlphaFeatureEnabled = isFeatureEnabled('contribution-alpha');

/** Contribution Beta FF */
export const isContributionBetaFeatureEnabled = isFeatureEnabled('contribution-beta');

/** New simple template mode enabled */
export const isNewSimpleTemplateFeatureEnabled = isFeatureEnabled('new-simple-template-experience');
/** New contribution experience enabled */
export const isNewContributionExperienceEnabled = isFeatureEnabled('new-contribution-experience');

/** Is Page Writer Editor enabled */
export const isNewWriterEditorEnabled = isFeatureEnabled('accessible-writer-editor');

/** Display content in child */
export const isDisplayContentInChildSiteEnabled = isFeatureEnabled('display-parent-content-in-child');

/** Should user be redirected to writer editor. Only happens if the user is strictly a writer (no designer rights). */
export const shouldUserBeRedirectedToWriterEditor = (contentTypeId?: string) =>
    createSelector(
        [isNewWriterEditorEnabled, canUserEditCurrentContent, canUserDesignCurrentContent(contentTypeId)],
        (isWriterEditorEnabled, isContentEditor, isContentDesigner) =>
            Boolean(contentTypeId) && isWriterEditorEnabled && isContentEditor && !isContentDesigner,
    );
